import { atom } from 'jotai';
import axiosService from './axios.service';
import { store } from './atom.service';

export interface SessionPerson {
    _id: string;
    username: string;
    email: string;
}

export interface SessionPayload {
    _id: string;
    hackathon: string;
    name: string;
    slug: string;
    // markdown; may contain meeting links and room locations
    description: string;
    descriptionUpdatedAt: Date | null;
    // category ids (hackathon.categoryMeta[]._id)
    categories: string[];
    judges: SessionPerson[];
    hosts: SessionPerson[];
    // idea ids, in presenting order
    ideas: string[];
    // follows the hackathon automatically instead of an explicit list (the default "Main room" follows all)
    allJudges: boolean;
    allCategories: boolean;
    allIdeas: boolean;
    position: number;
}

export interface SessionChanges {
    name?: string;
    slug?: string;
    description?: string;
    categories?: string[];
    judges?: string[];
    hosts?: string[];
    ideas?: string[];
    allJudges?: boolean;
    allCategories?: boolean;
    allIdeas?: boolean;
}

// sessions per hackathon id
export const sessionsAtom = atom<Record<string, SessionPayload[]>>({});

const convert = (data: any): SessionPayload => ({
    ...data,
    descriptionUpdatedAt: data.descriptionUpdatedAt ? new Date(data.descriptionUpdatedAt) : null
});

const storeSessions = (hackathonId: string, sessions: SessionPayload[]) => {
    store.set(sessionsAtom, current => ({ ...current, [hackathonId]: sessions }));
    return sessions;
}

const storeSession = (hackathonId: string, session: SessionPayload) => {
    store.set(sessionsAtom, current => {
        const list = current[hackathonId] || [];
        const exists = list.some(s => s._id === session._id);
        return {
            ...current,
            [hackathonId]: exists ? list.map(s => s._id === session._id ? session : s) : [...list, session]
        };
    });
    return session;
}

const base = (hackathonId: string) => `/api/hackathons/${hackathonId}/sessions`;

// components mounting together ask for the same list; share the request in flight
const inflight = new Map<string, Promise<SessionPayload[]>>();

export const getSessions = (hackathonId: string) => {
    const existing = inflight.get(hackathonId);
    if (existing) {
        return existing;
    }
    const request = axiosService.get(base(hackathonId))
        .then(({ data }) => storeSessions(hackathonId, data.map(convert)))
        .finally(() => inflight.delete(hackathonId));
    inflight.set(hackathonId, request);
    return request;
}

export const createSession = async (hackathonId: string, changes: SessionChanges) => {
    const { data } = await axiosService.post(base(hackathonId), changes);
    return storeSession(hackathonId, convert(data));
}

/**
 * notify: tell everyone in the session about a description change
 */
export const updateSession = async (hackathonId: string, sessionId: string, changes: SessionChanges, notify = false) => {
    const { data } = await axiosService.put(`${base(hackathonId)}/${sessionId}`, { ...changes, notify });
    return { session: storeSession(hackathonId, convert(data.session)), notified: data.notified as number };
}

export const deleteSession = async (hackathonId: string, sessionId: string) => {
    await axiosService.delete(`${base(hackathonId)}/${sessionId}`);
    store.set(sessionsAtom, current => ({
        ...current,
        [hackathonId]: (current[hackathonId] || []).filter(s => s._id !== sessionId)
    }));
}

export const autoAssignSessions = async (hackathonId: string, minJudges: number) => {
    const { data } = await axiosService.post(`${base(hackathonId)}/autoassign`, { minJudges });
    return storeSessions(hackathonId, data.map(convert));
}

export const notifySessionAssignments = async (hackathonId: string) => {
    const { data } = await axiosService.post(`${base(hackathonId)}/notify`);
    return data.sent as number;
}

/**
 * Valid URL name from a session name: lowercase letters, numbers and single dashes.
 */
export const slugify = (name: string) => name
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/, '');
